import React from "react"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from "@/components/ui/table"
import { tableHeadClass, tableCellClass } from "@/components/ui/table-styles"
import PaginationButton from "@/components/ui/pagination-button"
import { FileText } from "lucide-react"
import dayjs from "dayjs"
import { Level, StockPointType } from "@/gen/auth/common_pb"
import { toast } from "@/components/ui/use-toast"
import { useRouter } from "@tanstack/react-router"
import { PurchaseReturnLineItem } from "@/gen/purchase_return/common_pb"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import DatePicker from "@/components/ui/date-input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { pharmacyClient } from "@/connect"

const Details = ({
    data,
    currentStatus,
    purchaseReturnId
}: {
    data: PurchaseReturnLineItem[]
    currentStatus: string
    purchaseReturnId: string
}) => {
    const [currentPage, setCurrentPage] = React.useState(1)
    const totalPages = Math.ceil(data.length / 5)
    const paginatedItems = data.slice((currentPage - 1) * 10, currentPage * 10)

    const level = localStorage.getItem('level')
    const stockPointType = localStorage.getItem('stockPointType')
    const router = useRouter()

    const [curretnState, setCurrentState] = React.useState<'itemsList' | 'creditNoteInfo'>('itemsList')

    const [creditNoteNumber, setCreditNoteNumber] = React.useState('')
    const [creditNoteAmount, setCreditNoteAmount] = React.useState('')
    const [issueDate, setIssueDate] = React.useState<Date | undefined>(undefined)
    const [remarks, setRemarks] = React.useState('')
    const [paymentType, setPaymentType] = React.useState<'credit_note' | 'others'>('others')
    const [loading, setLoading] = React.useState(false)

    // Pagination logic for ellipsis
    const getPageNumbers = () => {
        const pages = []
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            pages.push(1)
            if (currentPage > 3) pages.push('...')
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
                pages.push(i)
            }
            if (currentPage < totalPages - 2) pages.push('...')
            pages.push(totalPages)
        }
        return pages
    }

    return (
        <Dialog>
            <DialogTrigger>
                <Button variant="ghost" size="sm" className="flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Details
                </Button>
            </DialogTrigger>
            <DialogContent className="w-[90vw] max-w-2xl p-0 flex flex-col gap-0">
                <DialogHeader className="p-4 border-b">
                    <DialogTitle>Return Details</DialogTitle>
                    <DialogDescription>
                        Here are the details of the return. Please check from Supplier, if credit note is generated.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-1 p-4 overflow-auto">
                    {curretnState === 'itemsList' && <div className="border border-slate-200 rounded-lg bg-white">
                        <Table className="text-sm">
                            <TableHeader>
                                <TableRow className="text-sm">
                                    <TableHead className={tableHeadClass}>SL No.</TableHead>
                                    <TableHead className={tableHeadClass}>Product Name</TableHead>
                                    <TableHead className={tableHeadClass}>Batch Number</TableHead>
                                    <TableHead className={tableHeadClass}>Expiry</TableHead>
                                    <TableHead className={tableHeadClass}>Return Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedItems.map((item, idx) => (
                                    <TableRow className="font-medium" key={item.inventoryId}>
                                        <TableCell className={tableCellClass}>{(currentPage - 1) * 10 + idx + 1}</TableCell>
                                        <TableCell className={tableCellClass}>{item.itemName}</TableCell>
                                        <TableCell className={tableCellClass}>{item.batchNumber}</TableCell>
                                        <TableCell className={tableCellClass}>{dayjs(item.expiryDate).format("DD/MM/YYYY")}</TableCell>
                                        <TableCell className={tableCellClass}>{item.returnedQuantity}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between p-2 text-sm border-t bg-white rounded-b-lg">
                                <p>{data.length} <span className="text-gray-500 font-light">count</span></p>
                                <div className="flex items-center gap-1">
                                    <PaginationButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                        {'<<'}
                                    </PaginationButton>
                                    <PaginationButton onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                        {'<'}
                                    </PaginationButton>
                                    {getPageNumbers().map((page, idx) => (
                                        page === '...'
                                            ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                                            : <PaginationButton
                                                key={page}
                                                active={currentPage === page}
                                                onClick={() => setCurrentPage(page as number)}
                                                disabled={currentPage === page}
                                            >
                                                <span className="text-sm">{page}</span>
                                            </PaginationButton>
                                    ))}
                                    <PaginationButton onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                        {'>'}
                                    </PaginationButton>
                                    <PaginationButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                        {'>>'}
                                    </PaginationButton>
                                </div>
                            </div>
                        )}
                    </div>}
                    {curretnState === 'creditNoteInfo' && <div className="p-4">
                        <p className="text-sm text-gray-600 mb-2">Credit Note Information</p>
                        <Label className="text-sm font-medium">Payment Type for Return</Label>
                        <RadioGroup defaultValue={paymentType} onValueChange={(e) => {
                            setPaymentType(e as 'credit_note' | 'others')
                        }} className="flex gap-4 mt-2">
                            <div className="flex items-center gap-4">
                                <RadioGroupItem value="credit_note" id="credit_note"/>
                                <Label htmlFor="credit_note" className="text-sm font-medium">Credit Note</Label>
                                <RadioGroupItem value="others" id="others"/>
                                <Label htmlFor="others" className="text-sm font-medium">Others</Label>
                            </div>
                        </RadioGroup>
                        {paymentType === 'credit_note' && <div className="mt-4">
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col gap-1">
                                        <Label className="text-sm font-medium">Credit Note Number</Label>
                                        <Input type="text" className="border rounded p-2" placeholder="Enter Credit Note Number" 
                                            value={creditNoteNumber}
                                            onChange={(e) => setCreditNoteNumber(e.target.value)}
                                            pattern="^[a-zA-Z0-9-]+$" // Allows alphanumeric characters and hyphens
                                            title="Please enter a valid credit note number."
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Label className="text-sm font-medium">Credit Note Amount</Label>
                                        <Input type="number" className="border rounded p-2" placeholder="Enter Credit Note Amount" 
                                            value={creditNoteAmount}
                                            onChange={(e) => setCreditNoteAmount(e.target.value)}
                                            min="0"
                                            step="0.01"
                                            pattern="^\d+(\.\d{1,2})?$" // Allows up to two decimal places
                                            title="Please enter a valid amount, up to two decimal places."
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Label className="text-sm font-medium">Remarks</Label>
                                        <Input type="text" className="border rounded p-2" placeholder="Enter any remarks or notes" 
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            maxLength={200} // Limit to 200 characters
                                            title="Remarks should not exceed 200 characters."
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Label className="text-sm font-medium">Issue Date</Label>
                                        <DatePicker
                                            className="border rounded p-2 w-full"
                                            onSelect={(date) => {
                                                if (date) {
                                                    setIssueDate(date)
                                                } else {
                                                    setIssueDate(undefined)
                                                }
                                            }}
                                            selected={issueDate}
                                        />
                                    </div>
                                </div>
                        </div>}
                    </div>}
                </div>
                {(level === Level.STOCK_POINT.toString() && stockPointType === StockPointType.RECEIVING.toString()) && <div className="flex justify-end gap-2 p-2 border-t">
                    {currentStatus === 'APPROVED' && curretnState === 'itemsList' && <Button className="bg-blue-700 text-white hover:bg-blue-800" variant="default" onClick={async () => {
                        setCurrentState('creditNoteInfo')
                    }}>Next</Button>}
                    {currentStatus === 'APPROVED' && curretnState === 'creditNoteInfo' && <Button variant="outline" onClick={() => {
                        setCurrentState('itemsList')
                    }}>Back</Button>}
                    {currentStatus === 'APPROVED' && curretnState === 'creditNoteInfo' && <Button className="bg-blue-700 text-white hover:bg-blue-800" disabled={loading} variant="default" onClick={async () => {
                        try {
                            if (paymentType === 'credit_note' && (!creditNoteNumber || !creditNoteAmount || !issueDate)) {
                                toast({
                                    title: "Error",
                                    description: "Please fill all fields for Credit Note.",
                                    variant: "destructive"
                                })
                                return
                            }
                            setLoading(true)
                            await pharmacyClient.completePurchaseReturn({
                                purchaseReturnId: purchaseReturnId,
                                isCreditNoteIssued: paymentType === 'credit_note',
                                creditNoteInfo: {
                                    creditNoteNumber: creditNoteNumber,
                                    totalAmount: parseFloat(creditNoteAmount),
                                    inputIssueDate: issueDate ? dayjs(issueDate).unix() : undefined,
                                    notes: remarks
                                }
                            })
                            setLoading(false)
                            toast({
                                title: "Success",
                                description: "Return completed successfully.",
                                variant: "success",
                                duration: 3000
                            })
                            router.invalidate()
                        } catch (error) {
                            toast({
                                title: "Error",
                                description: "Failed to complete the return.",
                                variant: "destructive"
                            })
                            setLoading(false)
                        }
                    }}>Complete Return</Button>}
                </div>}
            </DialogContent>
        </Dialog>
    )
}

export default Details